/**
 * Garmin-Style Digital Display Widgets
 * Professional marine digital readouts for GPS coordinates, engine hours, and text-based data
 */

import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { theme } from '../../../styles/theme';

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
`;

const countUp = keyframes`
  from { transform: translateY(20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

// Base Digital Display Container
const DigitalDisplayContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  min-height: 120px;
  background: ${theme.colors.background.paper};
  border-radius: ${theme.borderRadius.lg};
  padding: 1rem;
  position: relative;
  border: 2px solid ${theme.colors.border.primary};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  animation: ${fadeIn} 0.5s ease-out;
`;

const DisplayTitle = styled.div`
  font-size: ${theme.fontSizes.xs};
  font-weight: ${theme.fontWeights.semibold};
  color: ${theme.colors.text.secondary};
  margin-bottom: 0.5rem;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const MainValue = styled.div`
  font-family: 'Monaco', 'Consolas', 'SF Mono', monospace;
  font-size: ${props => props.size || theme.fontSizes['2xl']};
  font-weight: ${theme.fontWeights.bold};
  color: ${props => props.color || theme.colors.text.primary};
  text-align: center;
  line-height: 1.2;
  margin-bottom: 0.25rem;
  animation: ${countUp} 0.8s ease-out;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
`;

const UnitLabel = styled.span`
  font-size: ${theme.fontSizes.sm};
  font-weight: ${theme.fontWeights.medium};
  color: ${theme.colors.text.secondary};
  margin-left: 0.25rem;
`;

const TrendIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 0.25rem;
  font-size: ${theme.fontSizes.xs};
  color: ${props => props.color || theme.colors.text.secondary};
  animation: ${props => props.animated ? pulse : 'none'} 2s infinite;
`;

const StatusIndicator = styled.div`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => props.color || '#10b981'};
  box-shadow: 0 0 8px ${props => props.color || '#10b981'}40;
`;

const MultiValueContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
`;

const ValueRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.25rem 0;
  border-bottom: 1px solid ${theme.colors.border.secondary};
  
  &:last-child {
    border-bottom: none;
  }
`;

const ValueLabel = styled.div`
  font-size: ${theme.fontSizes.xs};
  color: ${theme.colors.text.secondary};
  font-weight: ${theme.fontWeights.medium};
`;

const ValueDisplay = styled.div`
  font-family: 'Monaco', 'Consolas', monospace;
  font-size: ${theme.fontSizes.sm};
  font-weight: ${theme.fontWeights.bold};
  color: ${props => props.color || theme.colors.text.primary};
`;

// Utility functions
const formatValue = (value, precision = 1, unit = '') => {
  if (value === null || value === undefined) return '--';

  const num = typeof value === 'number' ? value : parseFloat(value);
  if (Number.isFinite(num)) {
    if (Math.abs(num) >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M${unit}`;
    }
    if (Math.abs(num) >= 1000) {
      return `${(num / 1000).toFixed(1)}k${unit}`;
    }
    return `${num.toFixed(precision)}${unit}`;
  }

  return `${value}${unit}`;
};

const getTrendIcon = (trend) => {
  if (trend > 0) return '↗';
  if (trend < 0) return '↘';
  return '→';
};

const getTrendColor = (trend) => {
  if (trend > 0) return '#10b981';
  if (trend < 0) return '#ef4444';
  return '#6b7280';
};

/**
 * Large Digital Display - Perfect for primary values like speed, heading
 */
export const GarminLargeDigitalDisplay = ({
  title = "Speed",
  value = 4.2,
  unit = "kts",
  precision = 1,
  color = theme.colors.text.primary,
  trend = null,
  status = 'connected',
  animated = false
}) => {
  const [displayValue, setDisplayValue] = useState(0);
  
  useEffect(() => {
    const timer = setTimeout(() => setDisplayValue(value), 100);
    return () => clearTimeout(timer);
  }, [value]);
  
  const statusColors = {
    'connected': '#10b981',
    'warning': '#f59e0b',
    'error': '#ef4444',
    'offline': '#6b7280'
  };
  
  return (
    <DigitalDisplayContainer>
      <StatusIndicator color={statusColors[status]} />
      <DisplayTitle>{title}</DisplayTitle>
      
      <MainValue color={color} size={theme.fontSizes['3xl']}>
        {formatValue(displayValue, precision)}
        <UnitLabel>{unit}</UnitLabel>
      </MainValue>
      
      {trend !== null && (
        <TrendIndicator 
          color={getTrendColor(trend)} 
          animated={animated}
        >
          {getTrendIcon(trend)} {Math.abs(trend).toFixed(1)}
        </TrendIndicator>
      )}
    </DigitalDisplayContainer>
  );
};

/**
 * Compact Digital Display - For secondary values in multi-widget layouts
 */
export const GarminCompactDigitalDisplay = ({
  title = "Depth",
  value = 12.5,
  unit = "m",
  precision = 1,
  color = theme.colors.text.primary,
  status = 'connected'
}) => {
  const [displayValue, setDisplayValue] = useState(0);
  
  useEffect(() => {
    const timer = setTimeout(() => setDisplayValue(value), 100);
    return () => clearTimeout(timer);
  }, [value]);
  
  const statusColors = {
    'connected': '#10b981',
    'warning': '#f59e0b',
    'error': '#ef4444',
    'offline': '#6b7280'
  };
  
  return (
    <DigitalDisplayContainer style={{ minHeight: '80px', padding: '0.75rem' }}>
      <StatusIndicator color={statusColors[status]} />
      <DisplayTitle style={{ fontSize: theme.fontSizes.xs, marginBottom: '0.25rem' }}>
        {title}
      </DisplayTitle>
      
      <MainValue color={color} size={theme.fontSizes.xl}>
        {formatValue(displayValue, precision)}
        <UnitLabel>{unit}</UnitLabel>
      </MainValue>
    </DigitalDisplayContainer>
  );
};

/**
 * GPS Coordinate Display - Specialized for latitude/longitude
 */
export const GarminGPSDisplay = ({
  latitude = 37.7749,
  longitude = -122.4194,
  format = 'decimal' // 'decimal' or 'dms' (degrees, minutes, seconds)
}) => {
  const formatCoordinate = (coord, isLongitude = false) => {
    if (format === 'dms') {
      const abs = Math.abs(coord);
      const degrees = Math.floor(abs);
      const minutes = Math.floor((abs - degrees) * 60);
      const seconds = ((abs - degrees) * 60 - minutes) * 60;
      
      const direction = coord >= 0 
        ? (isLongitude ? 'E' : 'N') 
        : (isLongitude ? 'W' : 'S');
      
      return `${degrees}°${minutes.toString().padStart(2, '0')}'${seconds.toFixed(1)}"${direction}`;
    } else {
      return `${coord.toFixed(6)}°`;
    }
  };
  
  return (
    <DigitalDisplayContainer>
      <DisplayTitle>GPS Position</DisplayTitle>
      
      <MultiValueContainer>
        <ValueRow>
          <ValueLabel>LAT</ValueLabel>
          <ValueDisplay color="#10b981">
            {formatCoordinate(latitude, false)}
          </ValueDisplay>
        </ValueRow>
        
        <ValueRow>
          <ValueLabel>LON</ValueLabel>
          <ValueDisplay color="#10b981">
            {formatCoordinate(longitude, true)}
          </ValueDisplay>
        </ValueRow>
      </MultiValueContainer>
    </DigitalDisplayContainer>
  );
};

/**
 * Time Display - For engine hours, trip time, etc.
 */
export const GarminTimeDisplay = ({
  title = "Engine Hours",
  hours = 1247,
  minutes = 32,
  showSeconds = false,
  seconds = 15,
  format = 'hours' // 'hours', 'elapsed', 'clock'
}) => {
  const formatTime = () => {
    if (format === 'hours') {
      return `${hours.toLocaleString()}:${minutes.toString().padStart(2, '0')}`;
    } else if (format === 'elapsed') {
      const totalMinutes = hours * 60 + minutes;
      const displayHours = Math.floor(totalMinutes / 60);
      const displayMins = totalMinutes % 60;
      return `${displayHours}h ${displayMins}m`;
    } else if (format === 'clock') {
      const time = new Date();
      time.setHours(hours, minutes, seconds || 0);
      return showSeconds 
        ? time.toLocaleTimeString()
        : time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
  };
  
  return (
    <DigitalDisplayContainer>
      <DisplayTitle>{title}</DisplayTitle>
      
      <MainValue color="#3b82f6" size={theme.fontSizes['2xl']}>
        {formatTime()}
        {format === 'hours' && <UnitLabel>hrs</UnitLabel>}
      </MainValue>
    </DigitalDisplayContainer>
  );
};

/**
 * Multi-Value Digital Display - For related parameters
 */
export const GarminMultiValueDisplay = ({
  title = "Engine Monitor",
  values = [
    { label: 'RPM', value: 1850, unit: '', color: '#10b981' },
    { label: 'Temp', value: 185, unit: '°F', color: '#f59e0b' },
    { label: 'Oil', value: 40, unit: 'psi', color: '#10b981' }
  ]
}) => {
  return (
    <DigitalDisplayContainer>
      <DisplayTitle>{title}</DisplayTitle>
      
      <MultiValueContainer>
        {values.map((item, index) => (
          <ValueRow key={index}>
            <ValueLabel>{item.label}</ValueLabel>
            <ValueDisplay color={item.color}>
              {formatValue(item.value, 1)} {item.unit}
            </ValueDisplay>
          </ValueRow>
        ))}
      </MultiValueContainer>
    </DigitalDisplayContainer>
  );
};

/**
 * Status Display - For text-based status information
 */
export const GarminStatusDisplay = ({
  title = "System Status",
  status = "NORMAL",
  message = "All systems operational",
  statusColor = '#10b981',
  showIcon = true
}) => {
  const getStatusIcon = (status) => {
    switch (status.toLowerCase()) {
      case 'normal': case 'ok': case 'good': return '✓';
      case 'warning': case 'caution': return '⚠';
      case 'error': case 'alarm': case 'critical': return '✗';
      case 'offline': case 'disconnected': return '○';
      default: return '●';
    }
  };
  
  return (
    <DigitalDisplayContainer>
      <DisplayTitle>{title}</DisplayTitle>
      
      <div style={{ textAlign: 'center' }}>
        <MainValue color={statusColor} size={theme.fontSizes.xl}>
          {showIcon && (
            <span style={{ marginRight: '0.5rem', fontSize: theme.fontSizes.lg }}>
              {getStatusIcon(status)}
            </span>
          )}
          {status}
        </MainValue>
        
        {message && (
          <div style={{
            fontSize: theme.fontSizes.xs,
            color: theme.colors.text.secondary,
            marginTop: '0.5rem',
            lineHeight: 1.4
          }}>
            {message}
          </div>
        )}
      </div>
    </DigitalDisplayContainer>
  );
};

/**
 * Counter Display - For counts, quantities, etc.
 */
export const GarminCounterDisplay = ({
  title = "Waypoints",
  count = 47,
  maxCount = null,
  showProgress = false,
  animated = true
}) => {
  const [displayCount, setDisplayCount] = useState(0);
  
  useEffect(() => {
    if (animated) {
      const duration = 1000;
      const steps = 20;
      const increment = count / steps;
      let current = 0;
      
      const timer = setInterval(() => {
        current += increment;
        if (current >= count) {
          setDisplayCount(count);
          clearInterval(timer);
        } else {
          setDisplayCount(Math.floor(current));
        }
      }, duration / steps);
      
      return () => clearInterval(timer);
    } else {
      setDisplayCount(count);
    }
  }, [count, animated]);
  
  const percentage = maxCount ? (count / maxCount) * 100 : null;
  
  return (
    <DigitalDisplayContainer>
      <DisplayTitle>{title}</DisplayTitle>
      
      <MainValue color="#3b82f6" size={theme.fontSizes['3xl']}>
        {displayCount.toLocaleString()}
        {maxCount && (
          <UnitLabel>/ {maxCount.toLocaleString()}</UnitLabel>
        )}
      </MainValue>
      
      {showProgress && percentage !== null && (
        <div style={{
          width: '100%',
          height: '4px',
          background: theme.colors.border.secondary,
          borderRadius: '2px',
          marginTop: '0.5rem',
          overflow: 'hidden'
        }}>
          <div style={{
            width: `${percentage}%`,
            height: '100%',
            background: '#3b82f6',
            borderRadius: '2px',
            transition: 'width 0.8s ease-out'
          }} />
        </div>
      )}
    </DigitalDisplayContainer>
  );
};

/**
 * Compound Display - Digital value overlaid on analog gauge
 */
export const GarminCompoundDisplay = ({
  title = "Battery Voltage",
  value = 12.6,
  unit = "V",
  min = 10,
  max = 15,
  zones = [
    { min: 10, max: 12, color: '#ef4444' },
    { min: 12, max: 13, color: '#f59e0b' },
    { min: 13, max: 15, color: '#10b981' }
  ],
  precision = 1
}) => {
  const [displayValue, setDisplayValue] = useState(min);
  
  useEffect(() => {
    const timer = setTimeout(() => setDisplayValue(value), 200);
    return () => clearTimeout(timer);
  }, [value]);
  
  const percentage = Math.max(0, Math.min(100, ((displayValue - min) / (max - min)) * 100));
  const currentZone = zones.find(zone => displayValue >= zone.min && displayValue <= zone.max);
  const gaugeColor = currentZone?.color || '#6b7280';
  
  return (
    <DigitalDisplayContainer>
      <DisplayTitle>{title}</DisplayTitle>
      
      {/* Analog Arc Background */}
      <div style={{ position: 'relative', width: '80px', height: '40px', marginBottom: '1rem' }}>
        <svg width="80" height="40" viewBox="0 0 80 40">
          {/* Background arc */}
          <path
            d="M 10 35 A 30 30 0 0 1 70 35"
            fill="none"
            stroke={theme.colors.border.secondary}
            strokeWidth="4"
            strokeLinecap="round"
          />
          {/* Value arc */}
          <path
            d="M 10 35 A 30 30 0 0 1 70 35"
            fill="none"
            stroke={gaugeColor}
            strokeWidth="4"
            strokeLinecap="round"
            strokeDasharray={`${percentage * 0.94} 100`}
            style={{ transition: 'stroke-dasharray 0.8s ease-out' }}
          />
        </svg>
      </div>
      
      {/* Digital Overlay */}
      <MainValue color={gaugeColor} size={theme.fontSizes['2xl']}>
        {formatValue(displayValue, precision)}
        <UnitLabel>{unit}</UnitLabel>
      </MainValue>
    </DigitalDisplayContainer>
  );
};

export default {
  GarminLargeDigitalDisplay,
  GarminCompactDigitalDisplay,
  GarminGPSDisplay,
  GarminTimeDisplay,
  GarminMultiValueDisplay,
  GarminStatusDisplay,
  GarminCounterDisplay,
  GarminCompoundDisplay
};